import type { components } from "@/api-schema";
import { MergeEntityName } from "@/api-schema-enums";
import { useMergeMutation } from "@/common/api/merge/use-merge-execute.mutation";
import { useMergePreviewQuery } from "@/common/api/merge/use-merge-preview.query";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { Button, ButtonColorVariant, ButtonVariant } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader, LoaderSize, LoaderVariant } from "@/components/ui/loader";
import { GitMerge } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChoosePropertiesStep } from "./_components/steps/choose-properties-step";
import { PickTargetStep } from "./_components/steps/pick-target-step";
import { buildDefaultChoices } from "./_helpers/build-default-choices";

type MergeSelections = components["schemas"]["MergeExecuteBody"]["selections"];
export type MergeEntityRef = components["schemas"]["MergeEntitySummaryResponse"];

enum MergeStep {
  PickTarget = "PickTarget",
  ChooseProperties = "ChooseProperties",
}

export interface MergeDialogProps {
  open: boolean;
  entity: MergeEntityName;
  source: MergeEntityRef;
  sourceLogoUrl?: string;
  onClose: () => void;
}

export const MergeDialog = ({ open, entity, source, sourceLogoUrl, onClose }: MergeDialogProps) => {
  const { t } = useTranslation(["common", "masterdata"]);

  const [step, setStep] = useState<MergeStep>(MergeStep.PickTarget);
  const [targetUuid, setTargetUuid] = useState("");
  const [targetLabel, setTargetLabel] = useState("");
  const [targetLogoUrl, setTargetLogoUrl] = useState<string | undefined>(undefined);
  const [choices, setChoices] = useState<MergeSelections>({});

  const {
    data: preview,
    isFetching: isMergePreviewPending,
    refetch: refetchPreview,
  } = useMergePreviewQuery({
    input: targetUuid ? { entity, sourceUuid: source.uuid, targetUuid } : null,
    enabled: false,
  });
  const { mutateAsync: mergeExecuteMutation, isPending: isMergeExecutionPending } = useMergeMutation();

  const handleContinue = async () => {
    if (!targetUuid) return;

    await toastMutationStatus({
      mutationFn: async () => {
        const { data } = await refetchPreview({ throwOnError: true });
        if (!data) {
          throw new Error("Merge preview returned no data");
        }
        setChoices(buildDefaultChoices(data.fields));
      },
      errorMessage: t("masterdata:ErrorLoadingPreview"),
      onSuccess: () => {
        setStep(MergeStep.ChooseProperties);
      },
    });
  };

  const handleRecheckConflicts = async () => {
    if (!targetUuid) return;

    await toastMutationStatus({
      mutationFn: async () => {
        const { data } = await refetchPreview({ throwOnError: true });
        if (data) {
          setChoices((previousChoices) => ({ ...buildDefaultChoices(data.fields), ...previousChoices }));
        }
      },
      errorMessage: t("masterdata:ErrorLoadingPreview"),
    });
  };

  const handleMerge = async () => {
    if (!preview || !targetUuid) return;

    await toastMutationStatus({
      mutationFn: () => mergeExecuteMutation({ entity, sourceUuid: source.uuid, targetUuid, selections: choices }),
      successMessage: t("masterdata:RecordsMergedSuccessfully", {
        source: source.label,
        target: preview.target.label,
      }),
      errorMessage: t("masterdata:ErrorMergingRecords"),
      onSuccess: () => {
        onClose();
      },
    });
  };

  const hasConflicts = preview ? preview.conflicts.length > 0 : false;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="md:max-w-4xl lg:max-w-5xl" onClose={onClose}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitMerge className="h-4 w-4" />
            {t("masterdata:Merge")}
          </DialogTitle>
          <DialogDescription>
            {step === MergeStep.PickTarget
              ? t("masterdata:SelectTargetDescription")
              : t("masterdata:ChoosePropertiesDescription")}
          </DialogDescription>
        </DialogHeader>

        <div className="py-2">
          {step === MergeStep.PickTarget ? (
            <PickTargetStep
              entity={entity}
              source={source}
              targetUuid={targetUuid}
              targetLabel={targetLabel}
              onTargetChange={(uuid, label, logoUrl) => {
                setTargetUuid(uuid);
                setTargetLabel(label);
                setTargetLogoUrl(logoUrl);
              }}
            />
          ) : preview ? (
            <ChoosePropertiesStep
              preview={preview}
              choices={choices}
              sourceLogoUrl={sourceLogoUrl}
              targetLogoUrl={targetLogoUrl}
              onChoiceChange={(key, value) => setChoices((prev) => ({ ...prev, [key]: value }))}
              onRecheckConflicts={handleRecheckConflicts}
              isRecheckingConflicts={isMergePreviewPending}
            />
          ) : null}
        </div>

        <DialogFooter>
          <Button
            variant={ButtonVariant.Outlined}
            colorVariant={ButtonColorVariant.Secondary}
            onClick={step === MergeStep.PickTarget ? onClose : () => setStep(MergeStep.PickTarget)}
          >
            {step === MergeStep.PickTarget ? t("Cancel") : t("Back")}
          </Button>

          {step === MergeStep.PickTarget ? (
            <Button onClick={handleContinue} disabled={!targetUuid || isMergePreviewPending}>
              {isMergePreviewPending && (
                <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} inheritColor className="mr-2" />
              )}
              {t("masterdata:ContinueSelectValues")}
            </Button>
          ) : (
            <Button
              colorVariant={ButtonColorVariant.Error}
              onClick={handleMerge}
              disabled={hasConflicts || isMergeExecutionPending}
            >
              {isMergeExecutionPending && (
                <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} inheritColor className="mr-2 shrink-0" />
              )}
              {t("masterdata:MergeAndDelete")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
